import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Settings2, Pencil, FileSpreadsheet, Download } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { api, errMsg } from "@/lib/api";
import { extractFields, withSystem } from "@/lib/card";
import { buildPages } from "@/lib/exporter";
import { useExporter } from "@/lib/useExporter";
import RecordForm from "@/components/data/RecordForm";
import PreviewPane from "@/components/data/PreviewPane";
import FieldsDialog from "@/components/entry/FieldsDialog";
import CardsTable from "@/components/entry/CardsTable";
import CustomersView from "@/components/entry/CustomersView";
import CustomerHistoryDialog, { groupCustomers, customerKey as customerKeyOf } from "@/components/entry/CustomerHistory";
import ImportDialog from "@/components/data/ImportDialog";
import { Seg } from "@/components/editor/fields";

const LAYOUT_LABELS = { PURITY: "Purity in %", HALLMARK: "Showroom's logo", KARAT: "Karat value" };


export default function CardEntry() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [layoutId, setLayoutIdState] = useState(localStorage.getItem("pvc_entry_layout") || "");
  const [baseFields, setBaseFields] = useState([]);
  const [cards, setCards] = useState([]);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [layout, setLayout] = useState("card");
  const [sides, setSides] = useState("both");
  const [printers, setPrinters] = useState([]);
  const { run, job, stage } = useExporter();
  const [importRows, setImportRows] = useState(null);
  const [view, setView] = useState("cards");
  const [historyKey, setHistoryKey] = useState(null);
  const customers = useMemo(() => groupCustomers(cards), [cards]);
  const historyCustomer = customers.find((c) => c.key === historyKey) || null;
  const fileRef = useRef(null);

  const setLayoutId = (v) => { localStorage.setItem("pvc_entry_layout", v); setLayoutIdState(v); };
  const loadCards = useCallback(() => api.get("/cards").then((r) => setCards(r.data)), []);

  useEffect(() => {
    api.get("/templates").then((r) => {
      setTemplates(r.data);
      setLayoutIdState((cur) => (r.data.some((t) => t.id === cur) ? cur : r.data[0]?.id || ""));
    });
    api.get("/card-fields").then((r) => setBaseFields(r.data));
    api.get("/printers").then((r) => setPrinters(r.data));
    loadCards();
  }, [loadCards]);

  const tpl = templates?.find((t) => t.id === layoutId) || null;
  const fields = useMemo(() => {
    const keys = new Set(baseFields.map((f) => f.key));
    const extra = tpl ? extractFields(tpl).filter((f) => !keys.has(f.key)).map((f) => ({ ...f, label: LAYOUT_LABELS[f.key] || f.key.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) })) : [];
    return [...baseFields, ...extra];
  }, [baseFields, tpl]);

  const save = async () => {
    try {
      const body = { data: form, layout_id: layoutId || null };
      if (editingId) await api.put(`/cards/${editingId}`, body);
      else await api.post("/cards", body);
      toast.success(editingId ? "Card updated" : "Card saved");
      setForm({});
      setEditingId(null);
      loadCards();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const wb = XLSX.read(await f.arrayBuffer(), { raw: /\.csv$/i.test(f.name) });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "", raw: false });
      if (!rows.length) return toast.error("No rows found in file");
      setImportRows(rows);
    } catch { toast.error("Could not read file"); }
  };

  const downloadSample = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([fields.map((f) => f.key)]), "Cards");
    XLSX.writeFile(wb, "card_entry_template.csv", { bookType: "csv" });
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm(c.data);
    if (c.layout_id && templates.some((t) => t.id === c.layout_id)) setLayoutId(c.layout_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doPrint = (kind, list) => {
    if (!tpl) return toast.error("Choose a layout first");
    const items = list.map((c) => ({ key: c.id, data: withSystem(c) }));
    const bleed = +(localStorage.getItem("pvc_bleed") || 0);
    const calib = printers.find((p) => p.id === localStorage.getItem("pvc_printer")) || null;
    const s = tpl.double_sided === false ? "front" : sides;
    const pages = buildPages(tpl, items, { layout, sides: s, marks: layout === "a4", calib, bleed });
    run({ kind, tpl, pages, bleed, data: Object.fromEntries(items.map((i) => [i.key, i.data])), filename: `${tpl.name.replace(/\W+/g, "_")}_cards.pdf` });
  };

  if (!templates) return <div className="h-screen grid place-items-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-950">
      <AppHeader />
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-mono text-amber-400 uppercase tracking-[0.2em]">Card entry</p>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1" data-testid="card-entry-title">Fill, save & print cards</h1>
            <p className="text-sm text-slate-400 mt-1">Cards are saved once and can be printed with any layout.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Layout</label>
            <select className="cf-input h-9 text-sm w-64" value={layoutId} onChange={(e) => setLayoutId(e.target.value)} data-testid="entry-layout-select">
              {!templates.length && <option value="">No designs yet</option>}
              {templates.map((t) => {
                const dup = templates.filter((x) => x.name === t.name).length > 1;
                return <option key={t.id} value={t.id}>{t.name}{dup ? ` (${new Date(t.created_at).toLocaleDateString()} #${t.id.slice(0, 4)})` : ""} · {t.width}×{t.height}mm</option>;
              })}
            </select>
            {tpl && <button className="cf-btn-ghost h-9 text-xs" onClick={() => nav(`/editor/${tpl.id}`)} data-testid="entry-edit-layout-button"><Pencil size={13} /> Edit layout</button>}
            <button className="cf-btn-ghost h-9 text-xs" onClick={() => fileRef.current?.click()} data-testid="entry-import-button"><FileSpreadsheet size={13} /> Import CSV / Excel</button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onImportFile} data-testid="entry-import-input" />
            <button className="cf-btn-ghost h-9 text-xs" onClick={downloadSample} data-testid="entry-sample-csv-button"><Download size={13} /> Sample CSV</button>
            <button className="cf-btn-ghost h-9 text-xs" onClick={() => setFieldsOpen(true)} data-testid="entry-manage-fields-button"><Settings2 size={13} /> Manage fields</button>
          </div>
        </div>

        {!templates.length ? (
          <div className="rounded-xl border border-dashed border-slate-800 p-10 text-center text-sm text-slate-400" data-testid="entry-no-layouts">
            Create a design on the Designs page first, then come back to enter cards.
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
            <RecordForm fields={fields} form={form} setForm={setForm} autoKeys={["CERT_NO"]} editing={!!editingId} onSave={save}
              onCancel={() => { setForm({}); setEditingId(null); }} />
            {tpl && <PreviewPane tpl={tpl} data={withSystem({ id: editingId || "preview", data: form })} />}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/60 px-5 py-3" data-testid="entry-print-options">
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Print options</span>
          <div className="w-72"><Seg value={layout} onChange={setLayout} testId="entry-print-layout" options={[{ value: "card", label: "PVC card pages" }, { value: "a4", label: "A4 sheet" }]} /></div>
          <div className="w-64"><Seg value={sides} onChange={setSides} testId="entry-print-sides" options={[{ value: "both", label: "Front + Back" }, { value: "front", label: "Front" }, { value: "back", label: "Back" }]} /></div>
          <span className="text-[11px] text-slate-500">Bleed & printer calibration follow your Print page settings.</span>
          {job && <span className="text-xs font-mono text-amber-300 ml-auto" data-testid="entry-export-progress">Rendering {job.done}/{job.total}…</span>}
        </div>

        <div className="w-72">
          <Seg value={view} onChange={setView} testId="entry-view" options={[{ value: "cards", label: "All cards" }, { value: "customers", label: `Customers (${customers.length})` }]} />
        </div>
        {view === "cards" ? (
          <CardsTable cards={cards} fields={baseFields} templates={templates} editingId={editingId} busy={!!job} reload={loadCards}
            onEdit={startEdit} onPrint={doPrint} onCustomer={(c) => setHistoryKey(customerKeyOf(c))} />
        ) : (
          <CustomersView customers={customers} onOpen={(c) => setHistoryKey(c.key)} />
        )}
      </main>
      <CustomerHistoryDialog customer={historyCustomer} busy={!!job} onClose={() => setHistoryKey(null)} onPrint={doPrint}
        onEdit={(c) => { setHistoryKey(null); startEdit(c); }}
        onNew={(c) => { setHistoryKey(null); setEditingId(null); setForm({ CUSTOMER_NAME: c.name, MOBILE: c.mobile }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      <FieldsDialog open={fieldsOpen} onOpenChange={setFieldsOpen} fields={baseFields} onSaved={setBaseFields} />
      <ImportDialog rows={importRows} fields={fields} onClose={() => setImportRows(null)}
        onImport={(records) => api.post("/cards/bulk", { records, layout_id: layoutId || null }).then((r) => r.data.inserted)}
        onDone={() => { setImportRows(null); loadCards(); }} />
      {stage}
    </div>
  );
}
